/**
 * WidgetPreview — a live, faithful preview of the embeddable widget as visitors
 * will see it, rendered inside a bounded "browser window" frame on the config
 * editor. It mirrors the real widget's appearance knobs — launcher look
 * (bubble / bar / anchor / tab / hidden), size, corner position, brand color,
 * theme, and the drag/hotkey affordances — from the live branding form state, so
 * an admin sees the effect of every change instantly.
 *
 * This is a self-contained React mock (NOT the real Shadow-DOM bundle): it stays
 * bounded to the frame, needs no config server, and updates synchronously. The
 * geometry/colors intentionally track `apps/widget/src/ui/styles.ts`.
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import type { BrandingConfig, WidgetLauncher, WidgetSize } from '@hotline/contracts';

import { Segmented } from './ui/Segmented.js';

/** The branding subset the preview reflects (mirrors the editor's form state). */
export interface WidgetPreviewProps {
  launcher: WidgetLauncher;
  size: WidgetSize;
  position: NonNullable<BrandingConfig['position']>;
  primaryColor: string;
  accentColor: string;
  launcherText: string;
  theme: NonNullable<BrandingConfig['theme']>;
  draggable: boolean;
  hotkey: string;
}

/** Only allow a safe subset of characters into an inline color (mirror the widget). */
function safeColor(value: string, fallback: string): string {
  return value && /^[#a-zA-Z0-9(),.%\s-]+$/.test(value) ? value : fallback;
}

const SIZE_SCALE: Record<WidgetSize, number> = { small: 0.86, medium: 1, large: 1.18 };

/** Chat glyph — same path as the widget's inline icon set. */
function ChatIcon({ px }: { px: number }): ReactElement {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 11.5a8.4 8.4 0 0 1-12.3 7.4L3 21l2.1-5.7A8.4 8.4 0 1 1 21 11.5Z" />
    </svg>
  );
}

/** Absolute placement of the launcher/panel within the frame, by corner. */
function cornerStyle(position: WidgetPreviewProps['position'], inset = 14): CSSProperties {
  const vertical = position.startsWith('top') ? { top: inset } : { bottom: inset };
  const horizontal = position.endsWith('left') ? { left: inset } : { right: inset };
  return { position: 'absolute', ...vertical, ...horizontal };
}

function LauncherMark({
  branding,
  onClick,
}: {
  branding: WidgetPreviewProps;
  onClick: () => void;
}): ReactElement | null {
  const { launcher, size, position, primaryColor, launcherText } = branding;
  const scale = SIZE_SCALE[size];
  const primary = safeColor(primaryColor, '#5b5bd6');
  const label = launcherText || 'Feedback';
  const base: CSSProperties = {
    background: primary,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(20,20,40,.28)',
    fontWeight: 650,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  };

  if (launcher === 'hidden') return null;

  if (launcher === 'tab') {
    const side = position.endsWith('left') ? { left: 0 } : { right: 0 };
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        style={{
          ...base,
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          ...side,
          flexDirection: 'column',
          writingMode: 'vertical-rl',
          padding: `${16 * scale}px ${9 * scale}px`,
          borderRadius: position.endsWith('left') ? '0 12px 12px 0' : '12px 0 0 12px',
          fontSize: 13 * scale,
          letterSpacing: '.02em',
        }}
      >
        {label}
      </button>
    );
  }

  if (launcher === 'bar') {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        style={{
          ...base,
          ...cornerStyle(position),
          gap: 8 * scale,
          padding: `${11 * scale}px ${18 * scale}px`,
          borderRadius: 999,
          fontSize: 14 * scale,
        }}
      >
        <ChatIcon px={20 * scale} />
        {label}
      </button>
    );
  }

  if (launcher === 'anchor') {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        style={{
          ...base,
          ...cornerStyle(position),
          gap: 6 * scale,
          padding: `${6 * scale}px ${11 * scale}px`,
          borderRadius: 10,
          fontSize: 12.5 * scale,
        }}
      >
        <ChatIcon px={15 * scale} />
        {label}
      </button>
    );
  }

  // bubble (default): circular icon button.
  const d = 52 * scale;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{ ...base, ...cornerStyle(position), width: d, height: d, borderRadius: '50%' }}
    >
      <ChatIcon px={26 * scale} />
    </button>
  );
}

/** A faithful mock of the open feedback panel, tinted + themed. */
function PanelMock({
  branding,
  dark,
  onClose,
}: {
  branding: WidgetPreviewProps;
  dark: boolean;
  onClose: () => void;
}): ReactElement {
  const { size, position, primaryColor, accentColor, launcherText } = branding;
  const scale = SIZE_SCALE[size];
  const primary = safeColor(primaryColor, '#5b5bd6');
  const accent = safeColor(accentColor, '#8e8ef5');
  const width = Math.round(300 * scale);
  const surface = dark ? '#1c1d27' : '#ffffff';
  const fg = dark ? '#f3f4f8' : '#1a1a2e';
  const muted = dark ? '#9aa0ae' : '#6b7280';
  const border = dark ? '#2c2e3c' : '#e6e7ef';
  const fieldBg = dark ? '#14151c' : '#ffffff';

  return (
    <div
      role="dialog"
      aria-label="Feedback"
      style={{
        ...cornerStyle(position, 12),
        width,
        maxWidth: 'calc(100% - 24px)',
        background: surface,
        color: fg,
        border: `1px solid ${border}`,
        borderRadius: 16,
        boxShadow: '0 16px 48px rgba(20,20,40,.28)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '12px 14px',
          color: '#fff',
          background: `linear-gradient(135deg, ${primary}, color-mix(in srgb, ${primary} 78%, ${accent}))`,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 650 }}>
          <ChatIcon px={16} />
          {launcherText || 'Send feedback'}
        </span>
        <button
          type="button"
          aria-label="Close preview panel"
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
        >
          ✕
        </button>
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: muted }}>What happened?</div>
        <div
          style={{
            minHeight: 64,
            border: `1px solid ${border}`,
            borderRadius: 10,
            background: fieldBg,
            color: muted,
            padding: 10,
            fontSize: 12.5,
          }}
        >
          Describe the issue or share an idea…
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span
            style={{
              background: primary,
              color: '#fff',
              fontWeight: 650,
              padding: '8px 16px',
              borderRadius: 10,
              fontSize: 12.5,
            }}
          >
            Send
          </span>
        </div>
      </div>
    </div>
  );
}

export function WidgetPreview(branding: WidgetPreviewProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [scheme, setScheme] = useState<'light' | 'dark'>(
    branding.theme === 'dark' ? 'dark' : 'light',
  );

  // Follow the theme selector; `auto` previews as light (with a manual override).
  useEffect(() => {
    setScheme(branding.theme === 'dark' ? 'dark' : 'light');
  }, [branding.theme]);

  const dark = scheme === 'dark';
  const pageBg = dark ? '#0f1016' : '#eef0f6';

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] font-bold text-text">Live preview</div>
        <div className="flex items-center gap-2">
          <Segmented
            aria-label="Preview state"
            value={open ? 'open' : 'closed'}
            onValueChange={(v) => setOpen(v === 'open')}
            options={[
              { value: 'closed', label: 'Launcher' },
              { value: 'open', label: 'Panel' },
            ]}
          />
          <Segmented
            aria-label="Preview theme"
            value={scheme}
            onValueChange={(v) => setScheme(v as 'light' | 'dark')}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </div>
      </div>

      {/* Bounded browser-window frame. */}
      <div className="overflow-hidden rounded-xl border border-border shadow-card">
        <div className="flex items-center gap-1.5 border-b border-border bg-surface-2 px-3 py-2">
          <span className="size-2.5 rounded-full bg-[#ff5f57]" />
          <span className="size-2.5 rounded-full bg-[#febc2e]" />
          <span className="size-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-2 truncate rounded bg-surface px-2 py-0.5 text-[11px] text-faint">
            your-site.example.com
          </span>
        </div>
        <div className="relative h-[320px] w-full" style={{ background: pageBg }} aria-hidden={false}>
          {/* Faux page content so the widget has something to sit over. */}
          <div className="pointer-events-none absolute inset-0 flex flex-col gap-3 p-6 opacity-60">
            <div className="h-4 w-1/3 rounded bg-black/10 dark:bg-white/10" />
            <div className="h-2.5 w-3/4 rounded bg-black/10 dark:bg-white/10" />
            <div className="h-2.5 w-2/3 rounded bg-black/10 dark:bg-white/10" />
            <div className="h-2.5 w-1/2 rounded bg-black/10 dark:bg-white/10" />
          </div>

          {open ? (
            <PanelMock branding={branding} dark={dark} onClose={() => setOpen(false)} />
          ) : (
            <LauncherMark branding={branding} onClick={() => setOpen(true)} />
          )}

          {/* Affordance hints. */}
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            {branding.draggable ? (
              <span className="rounded-full bg-black/70 px-2 py-0.5 text-[10.5px] font-semibold text-white">
                ✥ Draggable
              </span>
            ) : null}
            {branding.launcher === 'hidden' ? (
              <span className="rounded-full bg-black/70 px-2 py-0.5 text-[10.5px] font-semibold text-white">
                ⌨ Opens with {branding.hotkey || 'Ctrl/⌘+Shift+F'}
              </span>
            ) : branding.hotkey ? (
              <span className="rounded-full bg-black/70 px-2 py-0.5 text-[10.5px] font-semibold text-white">
                ⌨ {branding.hotkey}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <p className="text-[11.5px] text-faint">
        A faithful preview — the real widget renders in an isolated shadow root on your site.
      </p>
    </div>
  );
}
